import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState, useEffect, useRef } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import * as Yup from 'yup';
import Select from "react-select";
import {
    AddNewEmployee,
    EditEmployeeDetails,
    GetKPICategories,
    GetDesignations,
    GetCircleByZoneId,
    GetEmployeeTypes,
    GetActiveDepartments,
    GetVehicleType
} from "../AttendanceApis";

import { formatDate, formattedDateForBE, getCurrentDate } from "../../../Common/helper/helpar";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { bouncy } from "ldrs";

bouncy.register();

const getMaxDateOfBirthFor18Plus = () => {
    const today = new Date();
    const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    const year = maxDate.getFullYear();
    const month = String(maxDate.getMonth() + 1).padStart(2, "0");
    const day = String(maxDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const weekDays = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' },
];

export const AddEditEmployee = ({
    showModal,
    closeModal,
    zoneData,
    circleData,
    editData,
    isEditEmployee,
    setIsEditEmployee,
    fetchEmployee,
    operationsData,
    designationsData,
    identityTypesData,
    shiftsData,
    transferStationsData,
    mobileDeviceData,
    kpiCategoryData,
    wardData
}) => {
    const normalizeServerMessage = (msg) => {
        if (!msg) return "Something went wrong.";
        try {
            const str = typeof msg === 'string' ? msg : JSON.stringify(msg);
            return str.replace(/Mobile Number/gi, 'Contact Number')
                .replace(/Mobile/gi, 'Contact')
                .replace(/mobile/gi, 'contact');
        } catch (e) {
            return String(msg);
        }
    };
    const [sameAsCurrentAddress, setSameAsCurrentAddress] = useState(false);
    const [loading, setLoading] = useState(false);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
    const [employeeTypes, setEmployeeTypes] = useState([]);
    const [circleOptions, setCircleOptions] = useState([]);
    const formikRef = useRef(null);

    const getValueByProperty = (filterValue, array, filterKey, returnKey) => {
        if (!array || !Array.isArray(array)) return "";
        const filteredItems = array.filter(item => item[filterKey] == filterValue);
        return filteredItems[0]?.[returnKey] ?? "";
    };

    const stringToArray = (string) => {
        const array = string.split(",");
        return array.map(item => {
            const cleaned = item.trim().replace(/^["']+|["']+$/g, '');
            return { value: cleaned, label: cleaned };
        });
    };

    // ─── FIX: Return circles so callers can .then() on them ───────────────────
    const fetchCirclesByZone = async (zoneId) => {
        if (!zoneId) {
            setCircleOptions([]);
            return [];
        }
        const data = await GetCircleByZoneId(zoneId);
        const circles = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data?.result)
                    ? data.result
                    : [];
        setCircleOptions(circles);
        return circles;
    };

    const getActiveZones = () =>
        zoneData?.filter(
            (zone) =>
                zone.isActive !== false &&
                String(zone.zoneName || "").toLowerCase().includes("delhi")
        ) ?? [];

    const resolveEditZone = () => {
        const zones = getActiveZones();
        if (!zones.length) return null;

        const byId = editData?.zoneId ?? editData?.ZoneId;
        if (byId != null && byId !== "") {
            const match = zones.find((z) => String(z.zoneId) === String(byId));
            if (match) return match;
        }

        const zoneName = String(editData?.zoneName ?? editData?.ZoneName ?? "").trim().toLowerCase();
        if (zoneName) {
            const match = zones.find(
                (z) => String(z.zoneName || "").trim().toLowerCase() === zoneName
            );
            if (match) return match;
        }

        return zones[0] ?? null;
    };

    const getDefaultZoneId = () => {
        if (isEditEmployee) {
            const matched = resolveEditZone();
            return matched?.zoneId != null ? String(matched.zoneId) : "";
        }
        const firstZone = getActiveZones()[0];
        return firstZone?.zoneId != null ? String(firstZone.zoneId) : "";
    };

    // ─── Load static lookup data once on mount ────────────────────────────────
    useEffect(() => {
        const fetchDepartments = async () => {
            const res = await GetActiveDepartments();
            const data = res?.data ?? res;
            setDepartmentOptions(Array.isArray(data) ? data : []);
        };
        const fetchDesignations = async () => {
            const data = await GetDesignations();
            setDesignationOptions(Array.isArray(data) ? data : []);
        };
        const fetchVehicleTypes = async () => {
            const data = await GetVehicleType();
            setVehicleTypeOptions(Array.isArray(data) ? data : []);
        };
        const fetchEmployeeTypes = async () => {
            const data = await GetEmployeeTypes();
            const mapped = Array.isArray(data)
                ? data.map((et) => ({
                    value: et.employeeTypeId,
                    label: et.employeeTypeName,
                }))
                : [];
            setEmployeeTypes(mapped);
        };
        fetchDepartments();
        fetchDesignations();
        fetchVehicleTypes();
        fetchEmployeeTypes();
    }, []);

    // ─── On edit: fetch circles for the employee's zone, then auto-select CircleId ──
    // FIX: was depending on editData?.zoneId (doesn't exist in API response).
    // Now resolves zoneId from zoneData by matching zoneName, then fetches circles,
    // then sets CircleId by matching circleName from the returned list.
    // useEffect(() => {
    //     if (!isEditEmployee || !editData?.zoneName || !zoneData?.length) return;

    //     const matchedZone = zoneData.find(z => z.zoneName === editData.zoneName);
    //     if (!matchedZone?.zoneId) return;

    //     fetchCirclesByZone(matchedZone.zoneId).then((circles) => {
    //         if (!formikRef.current) return;
    //         // Try matching by circleName first, fall back to circleId if API ever returns it
    //         const matchedCircle = circles.find(
    //             c => c.circleName === editData?.circleName
    //         ) ?? circles.find(
    //             c => c.circleId == editData?.circleId
    //         );
    //         if (matchedCircle) {
    //             setTimeout(() => {
    //                 formikRef.current?.setFieldValue('CircleId', matchedCircle.circleId);
    //             }, 50);
    //         }
    //     });
    // }, [isEditEmployee, editData?.zoneName, zoneData]);

    // ─── Edit: resolve Zone, load Ward options, preselect Ward ───────────────
    useEffect(() => {
        if (!isEditEmployee || !showModal || !zoneData?.length) return;

        const matchedZone = resolveEditZone();
        if (!matchedZone?.zoneId) return;

        const zoneId = String(matchedZone.zoneId);

        const loadEditZoneAndWard = async () => {
            if (formikRef.current) {
                formikRef.current.setFieldValue("ZoneId", zoneId, false);
                formikRef.current.setFieldError("ZoneId", undefined);
            }

            const circles = await fetchCirclesByZone(zoneId);
            const activeCircles = (circles || []).filter((c) => c.isActive !== false);

            const wardHint = String(
                editData?.wardNo ?? editData?.wardName ?? editData?.circleName ?? ""
            )
                .trim()
                .toLowerCase();
            const circleIdHint =
                editData?.circleId ?? editData?.CircleId ?? editData?.wardId ?? "";

            const circleMatch =
                activeCircles.find((c) => String(c.circleId) === String(circleIdHint)) ||
                activeCircles.find(
                    (c) => String(c.circleName || "").trim().toLowerCase() === wardHint
                ) ||
                // fallback via wardData if parent already fetched wards
                (() => {
                    const wardMatch = wardData?.find(
                        (w) =>
                            String(w.wardId) === String(editData?.wardId) ||
                            String(w.wardName || "").trim().toLowerCase() === wardHint ||
                            String(w.wardNo || "").trim().toLowerCase() === wardHint
                    );
                    if (!wardMatch?.circleId) return null;
                    return activeCircles.find(
                        (c) => String(c.circleId) === String(wardMatch.circleId)
                    );
                })();

            if (circleMatch && formikRef.current) {
                formikRef.current.setFieldValue(
                    "CircleId",
                    String(circleMatch.circleId),
                    false
                );
            }
        };

        loadEditZoneAndWard();
    }, [isEditEmployee, showModal, zoneData, editData, wardData]);

    useEffect(() => {
        if (isEditEmployee || !showModal) return;

        const activeZones = getActiveZones();
        if (activeZones.length === 0) return;

        const firstZoneId = String(activeZones[0].zoneId);
        fetchCirclesByZone(firstZoneId);

        // Keep Formik ZoneId in sync (browser can show first option while value is still "")
        if (formikRef.current) {
            const currentZoneId = formikRef.current.values?.ZoneId;
            if (!currentZoneId) {
                formikRef.current.setFieldValue("ZoneId", firstZoneId, false);
                formikRef.current.setFieldError("ZoneId", undefined);
            }
        }
    }, [isEditEmployee, showModal, zoneData]);

    // ─── Department ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isEditEmployee || !departmentOptions.length || !formikRef.current) return;
        const match = departmentOptions.find(d => d.departmentName === editData?.departmentName);
        if (!match) return;
        setTimeout(() => {
            formikRef.current?.setFieldValue('OperationId', match.departmentId);
            // Re-set designation after department so the filtered list is correct
            setTimeout(() => {
                const designationMatch = designationOptions.find(
                    d => d.designationName === editData?.designationName
                );
                if (designationMatch) {
                    formikRef.current?.setFieldValue('DesignationId', designationMatch.designationId);
                }
            }, 50);
        }, 100);
    }, [departmentOptions, editData?.departmentName]);

    // ─── Designation ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isEditEmployee || !designationOptions.length || !formikRef.current) return;
        const match = designationOptions.find(d => d.designationName === editData?.designationName);
        if (!match) return;
        setTimeout(() => {
            formikRef.current?.setFieldValue('DesignationId', match.designationId);
        }, 200); // after department settles
    }, [designationOptions, editData?.designationName]);

    useEffect(() => {
        console.log("🔵 wardData:", wardData);
        console.log("🔵 isEditEmployee:", isEditEmployee);
        console.log("🔵 editData?.wardNo:", editData?.wardNo);
        console.log("🔵 formikRef.current:", formikRef.current);
    }, [wardData, isEditEmployee, editData]);

    // ─── WardId only (Zone/CircleId handled by edit zone effect above) ────────
    useEffect(() => {
        if (!isEditEmployee || !wardData?.length || !formikRef.current) return;

        const wardMatch = wardData.find(
            (w) =>
                w.wardName?.trim() === editData?.wardNo?.trim() ||
                w.wardNo?.trim() === editData?.wardNo?.trim() ||
                String(w.wardId) === String(editData?.wardId)
        );

        if (!wardMatch) return;

        setTimeout(() => {
            formikRef.current?.setFieldValue("WardId", wardMatch.wardId);
        }, 100);
    }, [wardData, editData?.wardNo, editData?.wardId]);

    // ─── KPI Category ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isEditEmployee || !kpiCategoryData?.length || !formikRef.current) return;
        const kpiMatch = kpiCategoryData.find(
            k => k.kpicode === editData?.kpiCategoryCode || k.kpiCode === editData?.kpiCategoryCode || k.kpiCode === editData?.kpiCode || k.kpiCode === editData?.kpiCategory
        );
        if (!kpiMatch) return;
        setTimeout(() => {
            formikRef.current?.setFieldValue('KPICategoryId', kpiMatch.kpitypeId ?? kpiMatch.kpiTypeId);
        }, 100);
    }, [kpiCategoryData, editData?.kpiCategoryCode, editData?.kpiCode, editData?.kpiCategory]);

    useEffect(() => {
        if (!isEditEmployee || !identityTypesData?.length || !formikRef.current) return;
        if (editData?.identityType) return;
        const identityMatch = identityTypesData.find(
            it => it.identityTypeName === editData?.identityTypeName
        );
        if (!identityMatch) return;
        setTimeout(() => {
            formikRef.current?.setFieldValue('IdentityTypeID', identityMatch.identityTypeId);
        }, 100);
    }, [identityTypesData, editData?.identityTypeName, editData?.identityType]);

    // ─── FIX: EmployeeTypeId — API returns employeeTypeName (NOT employeeTypes) ──
    // Old code: depended on editData?.employeeTypes → undefined → never ran
    // Fix: use editData?.employeeTypeName
    useEffect(() => {
        if (!isEditEmployee || !editData?.employeeTypeName || !employeeTypes?.length || !formikRef.current) return;
        const matched = employeeTypes.find(
            opt => opt.label?.toLowerCase() === editData.employeeTypeName?.toLowerCase()
        );
        if (!matched) return;
        setTimeout(() => {
            formikRef.current?.setFieldValue('EmployeeTypeId', matched);
        }, 150);
    }, [employeeTypes, editData?.employeeTypeName]); // ✅ key fix: was editData?.employeeTypes

    // ─── FIX: VehicleType — set after vehicleTypeOptions loads ───────────────
    // Problem: initialValues runs before vehicleTypeOptions is populated from API.
    // With enableReinitialize=true this often resolves itself, but if vehicleType
    // is an empty string in the API, there's nothing to select — log it to confirm.
    useEffect(() => {
        if (!isEditEmployee || !vehicleTypeOptions.length || !formikRef.current) return;
        const vehicleTypeId = editData?.vehicleTypeId ?? editData?.vehicleType ?? "";
        if (!vehicleTypeId) return;
        const matched = vehicleTypeOptions.find(
            v => String(v.vehicleTypeId) === String(vehicleTypeId)
        );
        if (!matched) return;
        setTimeout(() => {
            formikRef.current?.setFieldValue('VehicleTypeId', String(matched.vehicleTypeId));
        }, 100);
    }, [vehicleTypeOptions, editData?.vehicleTypeId, editData?.vehicleType]);

    // ─── FIX: DeviceAvailability — smartDeviceAvailability / isSmartDeviceAvailable fallback ────
    useEffect(() => {
        if (!isEditEmployee || !formikRef.current) return;
        const availability = editData?.smartDeviceAvailability ?? editData?.isSmartDeviceAvailable;
        if (availability == null) return;
        setTimeout(() => {
            formikRef.current?.setFieldValue('DeviceAvailability', String(availability));
        }, 100);
    }, [isEditEmployee, editData?.smartDeviceAvailability, editData?.isSmartDeviceAvailable]);

    // ─── initialValues ────────────────────────────────────────────────────────
    const initialValues = {
        EmployeeName: isEditEmployee ? editData?.employeeName ?? "" : "",
        EmpID: isEditEmployee ? editData?.empId ?? editData?.tempId ?? "" : "",
        Remarks: isEditEmployee ? editData?.remarks ?? "" : "",         // ✅ was missing entirely

        // Department — resolved by useEffect (async options)
        OperationId: isEditEmployee
            ? departmentOptions?.find(d => d.departmentName === editData?.departmentName)?.departmentId ?? ""
            : "",

        // Designation — resolved by useEffect after OperationId settles
        DesignationId: isEditEmployee
            ? designationOptions?.find(d => d.designationName === editData?.designationName)?.designationId ?? ""
            : "",

        GuardianName: isEditEmployee ? editData?.guardianName ?? "" : "",

        // ✅ FIX: API returns contactNo, NOT mobileNo
        ContactNo: isEditEmployee ? editData?.contactNo ?? "" : "",

        CurrentAddress: isEditEmployee ? editData?.currentAddress ?? "" : "",
        PermanentAddress: isEditEmployee ? editData?.permanentAddress ?? "" : "",

        DateOfJoining: isEditEmployee
            ? editData?.dateOfJoining ? formattedDateForBE(editData.dateOfJoining) : ""
            : "",
        DateOfBirth: isEditEmployee
            ? editData?.dateOfBirth ? formattedDateForBE(editData.dateOfBirth) : ""
            : "",
        DateOfExit: isEditEmployee
            ? editData?.dateOfExit ? formattedDateForBE(editData.dateOfExit) : ""
            : "",

        MobileDeviceId: isEditEmployee ? editData?.mobileDeviceId ?? editData?.deviceId ?? "" : "",

        // KPI — resolved by useEffect
        KPICategoryId: isEditEmployee
            ? kpiCategoryData?.find(k =>
                k.kpicode === editData?.kpiCategoryCode || k.kpiCode === editData?.kpiCategoryCode || k.kpiCode === editData?.kpiCode || k.kpiCode === editData?.kpiCategory
            )?.kpitypeId ?? kpiCategoryData?.find(k =>
                k.kpicode === editData?.kpiCategoryCode || k.kpiCode === editData?.kpiCategoryCode || k.kpiCode === editData?.kpiCode || k.kpiCode === editData?.kpiCategory
            )?.kpiTypeId ?? ""
            : "",

        // Zone — resolved by matching zoneName
        ZoneId: getDefaultZoneId(),

        // Circle — resolved by useEffect after circles are fetched for the zone
        CircleId: isEditEmployee ? editData?.circleId ?? "" : "",

        // Ward — resolved by useEffect
        WardId: isEditEmployee
            ? wardData?.find(w =>
                String(w.wardId) === String(editData?.wardId) ||
                w.wardName === editData?.wardNo ||
                w.wardName === editData?.wardName ||
                w.wardNo === editData?.wardNo
            )?.wardId ?? ""
            : "",

        TransferStationId: isEditEmployee ? editData?.transferStationId ?? "" : "",
        Gender: isEditEmployee ? editData?.gender ?? "" : "",

        // identityType may come as an ID or the name in edit payload
        IdentityTypeID: isEditEmployee
            ? editData?.identityType ?? identityTypesData?.find(it => it.identityTypeName === editData?.identityTypeName)?.identityTypeId ?? ""
            : "",
        IdentityNo: isEditEmployee ? editData?.identityNo ?? "" : "",
        IdentityPDFUrl: isEditEmployee ? editData?.identityPDFUrl ?? null : "",

        WeekOff: isEditEmployee
            ? editData?.weekOff ? stringToArray(editData.weekOff) : []
            : [],

        // Shift — match by name
        ShiftId: isEditEmployee
            ? shiftsData?.find(s => s.shiftName === editData?.shiftName)?.shiftId ?? ""
            : "",

        IsActive: isEditEmployee
            ? (editData?.status === "Active" ? "true" : "false")
            : "true",

        // ✅ FIX: DeviceAvailability — null becomes "", useEffect fills it if non-null
        DeviceAvailability: isEditEmployee
            ? editData?.smartDeviceAvailability != null
                ? String(editData.smartDeviceAvailability)
                : editData?.isSmartDeviceAvailable != null
                    ? String(editData.isSmartDeviceAvailable)
                    : ""
            : "",

        // ✅ VehicleType — select by vehicleTypeId rather than name
        VehicleTypeId: isEditEmployee ? String(editData?.vehicleTypeId ?? editData?.vehicleType ?? "") : "",

        LicenceNumber: isEditEmployee ? editData?.driverLicenseNo ?? editData?.licenseNumber ?? "" : "",
        DriverLicenseValidity: isEditEmployee
            ? editData?.driverLicenseValidity
                ? formattedDateForBE(editData.driverLicenseValidity)
                : ""
            : "",

        // ✅ FIX: EmployeeTypeId — null here; useEffect sets it using employeeTypeName
        // EmployeeTypeId: null,
        EmployeeTypeId: isEditEmployee
            ? employeeTypes.find(
                opt => opt.label?.toLowerCase() === editData?.employeeTypeName?.toLowerCase()
            ) ?? null
            : null,
    };

    // ─── Validation ───────────────────────────────────────────────────────────
    const validationSchema = Yup.object({
        EmployeeName: Yup.string()
            .matches(/^[^\s]/, "Employee name cannot start with a space.")
            .matches(/^[A-Za-z\s]+$/, 'Employee name cannot contain special characters.')
            .min(3, "Employee name minimum contain 3 letters.")
            .required('Employee name is required'),
        EmpID: Yup.string()
            .trim()
            .matches(/^[A-Za-z0-9-]+$/, 'Emp ID can only contain letters, numbers and hyphen (-)')
            .required('Emp ID is required'),
        OperationId: Yup.string().required('Department is required'),
        DesignationId: Yup.string().required('Designation is required'),
        GuardianName: Yup.string()
            .matches(/^[^\s]/, "Guardian name cannot start with a space.")
            .matches(/^[A-Za-z\s]+$/, 'Guardian name cannot contain special characters.')
            .min(3, "Guardian name minimum contain 3 letters."),
        ContactNo: Yup.string()
            .typeError('Only numbers are allowed')
            .required('Contact no is required')
            .matches(/^[6-9]\d{9}$/, 'Mobile number should be a 10-digit number starting with 6, 7, 8, or 9'),
        CurrentAddress: Yup.string()
            .matches(/^[^\s]/, "Current address cannot start with a space.")
            .min(10, "Current address minimum contain 10 letters.")
            .required('Current address required'),
        PermanentAddress: Yup.string()
            .matches(/^[^\s]/, "Permanent address cannot start with a space.")
            .min(10, "Permanent address minimum contain 10 letters.")
            .required('Permanent address is required'),
        DateOfJoining: Yup.string().required('Date of joining is required'),
        DateOfBirth: Yup.string()
            .required('Date of birth is required')
            .test(
                'min-age',
                'Employee must be at least 18 years old',
                (value) => !value || value <= getMaxDateOfBirthFor18Plus()
            ),
        DateOfExit: Yup.date(),
        MobileDeviceId: Yup.string().required('Mobile Device ID is required'),
        Gender: Yup.string().required('Gender is required'),
        IdentityTypeID: Yup.string().required('Identity type ID is required'),
        IdentityNo: Yup.string()
            .required('Identity number is required')
            .matches(/^[a-zA-Z0-9]+$/, 'Identity number can only contain letters and numbers')
            .min(3, 'Identity number must be at least 3 characters')
            .max(20, 'Identity number must be at most 20 characters'),
        WeekOff: Yup.array().max(4, 'Week Off can select up to 4 days only'),
        ShiftId: Yup.string(),
        VehicleTypeId: Yup.string().required('Vehicle type is required'),
        ZoneId: Yup.string().required('Zone is required'),
        CircleId: Yup.string().required('Ward is required'),
        KPICategoryId: Yup.string(),
        EmployeeTypeId: Yup.object().nullable().required('Employee type is required'),
        IsActive: Yup.boolean(),
        LicenceNumber: Yup.string().when('EmployeeTypeId', {
            is: (employeeType) => String(employeeType?.value) === '5',
            then: (schema) => schema.trim().required('Driver licence number is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        DeviceAvailability: Yup.string().when('EmployeeTypeId', {
            is: (employeeType) => String(employeeType?.value) === '5',
            then: (schema) => schema.required('Smart device availability is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        DriverLicenseValidity: Yup.string().when('EmployeeTypeId', {
            is: (employeeType) => String(employeeType?.value) === '5',
            then: (schema) =>
                schema
                    .required('Driver license validity date is required')
                    .test(
                        'not-before-issue-date',
                        'Validity date cannot be before today',
                        (value) => !value || value >= getCurrentDate()
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    // ─── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async (values, { resetForm }) => {
        console.log(values, "values");

        if (isEditEmployee) {
            setLoading(true);

            const editPayloadValue = {
                ...values,
                EmpID: values.EmpID ?? "",
                TempId: undefined,
                EmployeeId: editData?.employeeId ?? editData?.EmployeeId ?? editData?.empId ?? "",
                DepartmentId: values.OperationId,
                DriverLicenseNo: values.LicenceNumber,
                DriverLicenseValidity: values.DriverLicenseValidity || null,
                EmployeeTypeId: values.EmployeeTypeId?.value ?? "",
                IdentityPDFUrl: editData.identityPDFUrl === values.IdentityPDFUrl ? null : values.IdentityPDFUrl,
                Status: values.IsActive === 'true',
                IsActive: undefined,
                IsSmartDeviceAvailable: values.DeviceAvailability === 'true',
                WeekOff: values.WeekOff.map(item => item.value).toString(),
                VehicleTypeId: values.VehicleTypeId,
                KpiCategoryId: values.KPICategoryId,
                IdentityTypeId: values.IdentityTypeID,
                VehicleType: undefined,
                KPICategoryId: undefined,
                IdentityTypeID: undefined,
            };
            console.log("🔴 Edit Employee Payload:", editPayloadValue);
            const response = await EditEmployeeDetails(editPayloadValue);
            if (response.status === 200) {
                toast.success(response?.data?.message);
                closeModal();
                setSameAsCurrentAddress(false);
                fetchEmployee();
                setIsEditEmployee(false);
                setLoading(false);
                resetForm();
            } else {
                toast.error(normalizeServerMessage(response?.response?.request?.responseText));
                setLoading(false);
            }
        } else {
            setLoading(true);

            const addPayloadValue = {
                ...values,
                EmpID: values.EmpID ?? "",
                TempId: undefined,
                DepartmentId: values.OperationId,
                DriverLicenseNo: values.LicenceNumber,
                DriverLicenseValidity: values.DriverLicenseValidity || null,
                EmployeeTypeId: values.EmployeeTypeId?.value ?? "",
                Status: values.IsActive === 'true',
                IsActive: undefined,
                IsSmartDeviceAvailable: values.DeviceAvailability === 'true',
                WeekOff: values.WeekOff.map(item => item.value).toString(),
                VehicleTypeId: values.VehicleTypeId,
                KpiCategoryId: values.KPICategoryId,
                IdentityTypeId: values.IdentityTypeID,
                VehicleType: undefined,
                KPICategoryId: undefined,
                IdentityTypeID: undefined,
            };
            console.log("🔴 Add Employee Payload:", addPayloadValue);
            const response = await AddNewEmployee(addPayloadValue);
            if (response.status === 200) {
                toast.success(response.data.message);
                closeModal();
                setSameAsCurrentAddress(false);
                fetchEmployee();
                setLoading(false);
                resetForm();
            } else {
                toast.error(normalizeServerMessage(response?.response?.data));
                setLoading(false);
            }
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <Modal
                size="lg"
                show={showModal}
                onHide={() => { closeModal(); setSameAsCurrentAddress(false); }}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header>
                    <Modal.Title>
                        {isEditEmployee ? "Edit" : "Add"} Employee
                    </Modal.Title>
                    <i
                        className="fa-solid fa-xmark close-container"
                        onClick={() => { closeModal(); setSameAsCurrentAddress(false); }}
                    ></i>
                </Modal.Header>
                <Modal.Body className="small">
                    <Formik
                        innerRef={formikRef}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize={true}
                    >
                        {({ handleChange, setFieldValue, setFieldTouched, setFieldError, values }) => (
                            <Form onSubmitCapture={() => console.log('AddEditEmployee: onSubmitCapture', formikRef.current?.values, formikRef.current?.errors)}>
                                <Row>
                                    {/* Employee Name */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="EmployeeName" className="form-label">
                                                Employee Name <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Employee Name"
                                                className="form-control"
                                                name="EmployeeName"
                                                id="EmployeeName"
                                                maxLength="100"
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                                    setFieldValue('EmployeeName', e.target.value);
                                                }}
                                            />
                                            <ErrorMessage name="EmployeeName" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Emp ID */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="EmpID" className="form-label">Emp ID <span style={{ color: '#EC1117' }}> *</span></label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Emp ID"
                                                className="form-control"
                                                name="EmpID"
                                                id="EmpID"
                                                maxLength="20"
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^A-Za-z0-9-]/g, '');
                                                    setFieldValue('EmpID', e.target.value);
                                                }}
                                            />
                                            <ErrorMessage name="EmpID" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Department
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="OperationId" className="form-label">
                                                Department <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                as="select"
                                                name="OperationId"
                                                id="OperationId"
                                                className="form-select"
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setFieldValue('OperationId', value, true);
                                                    setFieldValue('DesignationId', '', false);
                                                    setFieldError('OperationId', undefined);
                                                    setFieldTouched('OperationId', true, false);
                                                }}
                                            >
                                                <option value="">Select Department</option>
                                                {departmentOptions
                                                    .filter((dept) => dept.isActive)
                                                    .map((dept) => (
                                                        <option key={dept.departmentId} value={dept.departmentId}>
                                                            {dept.departmentName}
                                                        </option>
                                                    ))}
                                            </Field>
                                            <ErrorMessage name="OperationId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}

                                    {/* Department */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="OperationId" className="form-label">
                                                Department <span style={{ color: '#EC1117' }}> *</span>
                                            </label>

                                            <Select
                                                inputId="OperationId"
                                                name="OperationId"
                                                classNamePrefix="react-select"
                                                placeholder="Select Department"
                                                isClearable
                                                isSearchable
                                                options={departmentOptions
                                                    .filter((dept) => dept.isActive)
                                                    .map((dept) => ({
                                                        value: dept.departmentId,
                                                        label: dept.departmentName,
                                                    }))
                                                }
                                                value={
                                                    departmentOptions
                                                        .filter((dept) => dept.isActive)
                                                        .map((dept) => ({
                                                            value: dept.departmentId,
                                                            label: dept.departmentName,
                                                        }))
                                                        .find(
                                                            (option) =>
                                                                String(option.value) === String(values.OperationId)
                                                        ) || null
                                                }
                                                onChange={(selectedOption) => {
                                                    const value = selectedOption ? selectedOption.value : '';

                                                    setFieldValue('OperationId', value, true);

                                                    // Reset Designation when Department changes
                                                    setFieldValue('DesignationId', '', false);

                                                    // Clear Department error
                                                    setFieldError('OperationId', undefined);

                                                    // Mark Department as touched
                                                    setFieldTouched('OperationId', true, false);
                                                }}
                                                onBlur={() => {
                                                    setFieldTouched('OperationId', true, false);
                                                }}
                                                styles={{
                                                    control: (provided, state) => ({
                                                        ...provided,
                                                        minHeight: '38px',
                                                        backgroundColor: "#e9ecef",
                                                        borderColor: state.isFocused
                                                            ? '#86b7fe'
                                                            : '#dee2e6',
                                                        boxShadow: state.isFocused
                                                            ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                                                            : 'none',
                                                        '&:hover': {
                                                            borderColor: '#86b7fe',
                                                        },
                                                    }),
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        zIndex: 9999,
                                                    }),
                                                }}
                                            />

                                            <ErrorMessage
                                                name="OperationId"
                                                component="div"
                                                style={{ color: 'red' }}
                                            />
                                        </div>
                                    </Col>

                                    {/* Designation */}
                                    {/* <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="DesignationId" className="form-label">
                                                Designation <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                as="select"
                                                name="DesignationId"
                                                id="DesignationId"
                                                className="form-select"
                                            >
                                                <option value="">Select Designation</option>
                                                {designationOptions
                                                    .filter((d) =>
                                                        d.isActive &&
                                                        String(d.departmentId) === String(values.OperationId)
                                                    )
                                                    .map((d) => (
                                                        <option key={d.designationId} value={d.designationId}>
                                                            {d.designationName}
                                                        </option>
                                                    ))}
                                            </Field>
                                            <ErrorMessage name="DesignationId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}

                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="DesignationId" className="form-label">
                                                Designation <span style={{ color: '#EC1117' }}> *</span>
                                            </label>

                                            <Field name="DesignationId">
                                                {({ field, form }) => (
                                                    <Select
                                                        inputId="DesignationId"
                                                        name="DesignationId"
                                                        placeholder="Select Designation"
                                                        isSearchable
                                                        isClearable

                                                        options={designationOptions
                                                            .filter(
                                                                (d) =>
                                                                    d.isActive &&
                                                                    String(d.departmentId) === String(values.OperationId)
                                                            )
                                                            .map((d) => ({
                                                                value: String(d.designationId),
                                                                label: d.designationName,
                                                            }))}

                                                        value={
                                                            designationOptions
                                                                .filter(
                                                                    (d) =>
                                                                        d.isActive &&
                                                                        String(d.departmentId) === String(values.OperationId)
                                                                )
                                                                .map((d) => ({
                                                                    value: String(d.designationId),
                                                                    label: d.designationName,
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        String(option.value) === String(field.value)
                                                                ) || null
                                                        }

                                                        onChange={(selectedOption) => {
                                                            form.setFieldValue(
                                                                "DesignationId",
                                                                selectedOption ? selectedOption.value : ""
                                                            );
                                                        }}

                                                        onBlur={() => {
                                                            form.setFieldTouched("DesignationId", true);
                                                        }}

                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#fff",
                                                                zIndex: 9999,
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused
                                                                    ? "#f0f0f0"
                                                                    : "#fff",
                                                                color: "#212529",
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>

                                            <ErrorMessage
                                                name="DesignationId"
                                                component="div"
                                                style={{ color: 'red' }}
                                            />
                                        </div>
                                    </Col>
                                    {/* Employee Type */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="EmployeeTypeId" className="form-label">
                                                Employee Type <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Select
                                                classNamePrefix="CreatableSelect"
                                                id="EmployeeTypeId"
                                                name="EmployeeTypeId"
                                                options={employeeTypes}
                                                value={values.EmployeeTypeId}
                                                onChange={(selected) => {
                                                    setFieldValue('EmployeeTypeId', selected, true);
                                                    setFieldError('EmployeeTypeId', undefined);
                                                    setFieldTouched('EmployeeTypeId', true, false);
                                                    // Clear driver-only fields when employee type is not Driver (5)
                                                    if (String(selected?.value) !== '5') {
                                                        setFieldValue('LicenceNumber', '', false);
                                                        setFieldValue('DeviceAvailability', '', false);
                                                        setFieldValue('DriverLicenseValidity', '', false);
                                                    }
                                                }}
                                                placeholder="Select employee type"
                                                menuPlacement="auto"
                                                isMulti={false}
                                                isClearable={false}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        backgroundColor: "#e9ecef",
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        backgroundColor: '#fff',
                                                        zIndex: 9999,
                                                    }),
                                                    option: (base, state) => ({
                                                        ...base,
                                                        backgroundColor: state.isFocused ? '#f0f0f0' : '#fff',
                                                        color: '#212529',
                                                    }),
                                                }}
                                            />
                                            <ErrorMessage name="EmployeeTypeId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {String(values.EmployeeTypeId?.value) === '5' && (
                                        <>
                                            {/* Driver Licence Number */}
                                            <Col md="6" lg="4">
                                                <div className="form-group">
                                                    <label htmlFor="LicenceNumber" className="form-label">
                                                        Driver Licence Number <span style={{ color: '#EC1117' }}> *</span>
                                                    </label>
                                                    <Field
                                                        type="text"
                                                        placeholder="Enter Driver Licence Number"
                                                        className="form-control"
                                                        name="LicenceNumber"
                                                        id="LicenceNumber"
                                                        maxLength="50"
                                                        onInput={(e) => {
                                                            e.target.value = e.target.value.replace(/[^A-Za-z0-9\s]/g, '');
                                                            setFieldValue('LicenceNumber', e.target.value);
                                                        }}
                                                    />
                                                    <ErrorMessage name="LicenceNumber" component="div" style={{ color: 'red' }} />
                                                </div>
                                            </Col>
                                            {/* Smart Device Available */}
                                            <Col md="6" lg="4">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Smart Device Available <span style={{ color: '#EC1117' }}> *</span>
                                                    </label>
                                                    <Field name="DeviceAvailability">
                                                        {({ field, form }) => (
                                                            <Select
                                                                inputId="DeviceAvailability"
                                                                name="DeviceAvailability"
                                                                placeholder="Select Device Availability"
                                                                isSearchable
                                                                isClearable
                                                                options={[
                                                                    { value: "true", label: "Yes" },
                                                                    { value: "false", label: "No" },
                                                                ]}
                                                                value={[
                                                                    { value: "true", label: "Yes" },
                                                                    { value: "false", label: "No" },
                                                                ].find(option => option.value === field.value) || null}
                                                                onChange={(selectedOption) =>
                                                                    form.setFieldValue(
                                                                        'DeviceAvailability',
                                                                        selectedOption ? selectedOption.value : ''
                                                                    )
                                                                }
                                                                onBlur={() => form.setFieldTouched('DeviceAvailability', true)}
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        backgroundColor: '#fff',
                                                                    }),
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        backgroundColor: '#fff',
                                                                        zIndex: 9999,
                                                                    }),
                                                                    option: (base, state) => ({
                                                                        ...base,
                                                                        backgroundColor: state.isFocused ? '#f0f0f0' : '#fff',
                                                                        color: '#212529',
                                                                    }),
                                                                }}
                                                            />
                                                        )}
                                                    </Field>
                                                    <ErrorMessage name="DeviceAvailability" component="div" style={{ color: 'red' }} />
                                                </div>
                                            </Col>
                                            {/* Driver License Validity Date */}
                                            <Col md="6" lg="4">
                                                <div className="form-group">
                                                    <label htmlFor="DriverLicenseValidity" className="form-label">
                                                        Driver License Validity Date <span style={{ color: '#EC1117' }}> *</span>
                                                    </label>
                                                    <Field
                                                        type="date"
                                                        className="form-control"
                                                        name="DriverLicenseValidity"
                                                        id="DriverLicenseValidity"
                                                        min={getCurrentDate()}
                                                    />
                                                    <ErrorMessage name="DriverLicenseValidity" component="div" style={{ color: 'red' }} />
                                                </div>
                                            </Col>
                                        </>
                                    )}
                                    {/* Guardian Name */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="GuardianName" className="form-label">Guardian/Father Name</label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Guardian Name"
                                                className="form-control"
                                                name="GuardianName"
                                                id="GuardianName"
                                                maxLength="50"
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                                    setFieldValue('GuardianName', e.target.value);
                                                }}
                                            />
                                            <ErrorMessage name="GuardianName" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Contact Number */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="ContactNo" className="form-label">
                                                Contact Number <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Contact Number"
                                                className="form-control"
                                                maxLength={10}
                                                name="ContactNo"
                                                id="ContactNo"
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                                    setFieldValue('ContactNo', e.target.value);
                                                }}
                                            />
                                            <ErrorMessage name="ContactNo" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Current Address */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="CurrentAddress" className="form-label">
                                                Current Address <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Current Address"
                                                className="form-control"
                                                name="CurrentAddress"
                                                id="CurrentAddress"
                                                maxLength="500"
                                                onChange={(e) => {
                                                    setFieldValue("CurrentAddress", e.target.value);
                                                    sameAsCurrentAddress && setFieldValue('PermanentAddress', e.target.value);
                                                }}
                                            />
                                            <ErrorMessage name="CurrentAddress" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Permanent Address */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="PermanentAddress" className="form-label">
                                                Permanent Address <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Permanent Address"
                                                className="form-control"
                                                name="PermanentAddress"
                                                id="PermanentAddress"
                                                maxLength="500"
                                                disabled={sameAsCurrentAddress}
                                            />
                                            <label className="d-flex gap-1 align-items-center p-1">
                                                <input
                                                    type="checkbox"
                                                    value={sameAsCurrentAddress}
                                                    onChange={(e) => {
                                                        setFieldValue('PermanentAddress', e.target.checked ? values.CurrentAddress : "");
                                                        setSameAsCurrentAddress(e.target.checked);
                                                    }}
                                                />
                                                <span>Same as Current Address</span>
                                            </label>
                                            <ErrorMessage name="PermanentAddress" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Date of Birth */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="DateOfBirth" className="form-label">
                                                Date of Birth <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                type="date"
                                                className="form-control"
                                                name="DateOfBirth"
                                                id="DateOfBirth"
                                                max={getMaxDateOfBirthFor18Plus()}
                                                onChange={(e) => { handleChange(e); }}
                                            />
                                            <ErrorMessage name="DateOfBirth" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Mobile Device ID */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="MobileDeviceId" className="form-label">
                                                Mobile Device ID <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Mobile Device ID"
                                                className="form-control"
                                                name="MobileDeviceId"
                                                id="MobileDeviceId"
                                            />
                                            <ErrorMessage name="MobileDeviceId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>


                                    {/* Vehicle Type */}
                                    {/* <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label className="form-label">
                                                Vehicle Type <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                as="select"
                                                name="VehicleTypeId"
                                                id="VehicleTypeId"
                                                className="form-select"
                                            >
                                                <option value="">Select Vehicle type</option>
                                                {vehicleTypeOptions.map((v) => (
                                                    <option key={v.vehicleTypeId} value={String(v.vehicleTypeId)}>
                                                        {v.vehicleTypeName}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="VehicleTypeId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label className="form-label">
                                                Vehicle Type <span style={{ color: '#EC1117' }}> *</span>
                                            </label>

                                            <Field name="VehicleTypeId">
                                                {({ field, form }) => (
                                                    <Select
                                                        inputId="VehicleTypeId"
                                                        name="VehicleTypeId"
                                                        placeholder="Select Vehicle type"
                                                        isSearchable
                                                        isClearable
                                                        options={vehicleTypeOptions.map((v) => ({
                                                            value: String(v.vehicleTypeId),
                                                            label: v.vehicleTypeName,
                                                        }))}
                                                        value={
                                                            vehicleTypeOptions
                                                                .map((v) => ({
                                                                    value: String(v.vehicleTypeId),
                                                                    label: v.vehicleTypeName,
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        String(option.value) === String(field.value)
                                                                ) || null
                                                        }
                                                        onChange={(selectedOption) =>
                                                            form.setFieldValue(
                                                                'VehicleTypeId',
                                                                selectedOption ? selectedOption.value : ''
                                                            )
                                                        }
                                                        onBlur={() => form.setFieldTouched('VehicleTypeId', true)}
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: '#fff',
                                                                zIndex: 9999,
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused ? '#f0f0f0' : 'transparent',
                                                                color: '#212529',
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>

                                            <ErrorMessage
                                                name="VehicleTypeId"
                                                component="div"
                                                style={{ color: 'red' }}
                                            />
                                        </div>
                                    </Col>

                                    {/* Gender */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="Gender" className="form-label">
                                                Gender <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field name="Gender">
                                                {({ field, form }) => (
                                                    <Select
                                                        inputId="Gender"
                                                        name="Gender"
                                                        placeholder="Select Gender"
                                                        isSearchable
                                                        isClearable
                                                        options={[
                                                            { value: "Male", label: "Male" },
                                                            { value: "Female", label: "Female" },
                                                        ]}
                                                        value={[
                                                            { value: "Male", label: "Male" },
                                                            { value: "Female", label: "Female" },
                                                        ].find(option => option.value === field.value) || null}
                                                        onChange={(selectedOption) =>
                                                            form.setFieldValue(
                                                                'Gender',
                                                                selectedOption ? selectedOption.value : ''
                                                            )
                                                        }
                                                        onBlur={() => form.setFieldTouched('Gender', true)}
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: '#fff',
                                                                zIndex: 9999,
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused ? '#f0f0f0' : 'transparent',
                                                                color: '#212529',
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="Gender" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Identity Type */}
                                    {/* <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="IdentityTypeID" className="form-label">
                                                Identity Type ID <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                as="select"
                                                name="IdentityTypeID"
                                                id="IdentityTypeID"
                                                className="form-select"
                                            >
                                                <option value="">Select Identity Type ID</option>
                                                {identityTypesData &&
                                                    identityTypesData
                                                        .filter((identityType) => identityType.isActive)
                                                        .map((identityType) => (
                                                            <option key={identityType.identityTypeId} value={identityType.identityTypeId}>
                                                                {identityType.identityTypeName}
                                                            </option>
                                                        ))}
                                            </Field>
                                            <ErrorMessage name="IdentityTypeID" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}

                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="IdentityTypeID" className="form-label">
                                                Identity Type ID <span style={{ color: '#EC1117' }}> *</span>
                                            </label>

                                            <Field name="IdentityTypeID">
                                                {({ field, form }) => (
                                                    <Select
                                                        inputId="IdentityTypeID"
                                                        name="IdentityTypeID"
                                                        placeholder="Select Identity Type ID"
                                                        isSearchable
                                                        isClearable
                                                        options={
                                                            identityTypesData
                                                                ?.filter((identityType) => identityType.isActive)
                                                                .map((identityType) => ({
                                                                    value: identityType.identityTypeId,
                                                                    label: identityType.identityTypeName,
                                                                })) || []
                                                        }
                                                        value={
                                                            identityTypesData
                                                                ?.filter((identityType) => identityType.isActive)
                                                                .map((identityType) => ({
                                                                    value: identityType.identityTypeId,
                                                                    label: identityType.identityTypeName,
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        String(option.value) === String(field.value)
                                                                ) || null
                                                        }
                                                        onChange={(selectedOption) =>
                                                            form.setFieldValue(
                                                                'IdentityTypeID',
                                                                selectedOption ? selectedOption.value : ''
                                                            )
                                                        }
                                                        onBlur={() => form.setFieldTouched('IdentityTypeID', true)}
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: '#fff',
                                                                zIndex: 9999,
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused ? '#f0f0f0' : 'transparent',
                                                                color: '#212529',
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>

                                            <ErrorMessage
                                                name="IdentityTypeID"
                                                component="div"
                                                style={{ color: 'red' }}
                                            />
                                        </div>
                                    </Col>

                                    {/* Identity No */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="IdentityNo" className="form-label">
                                                Identity No <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <div className="position-relative">
                                                <Field
                                                    type="text"
                                                    placeholder="Enter Identity No"
                                                    className="form-control pe-5"
                                                    name="IdentityNo"
                                                    id="IdentityNo"
                                                    maxLength={20}
                                                    onInput={(e) => {
                                                        e.target.value = e.target.value
                                                            .replace(/[^a-zA-Z0-9]/g, '')
                                                            .slice(0, 20);
                                                        setFieldValue('IdentityNo', e.target.value);
                                                    }}
                                                />
                                            </div>
                                            {isEditEmployee && (<a href={editData.identityPDFUrl}>View File</a>)}
                                            {values.IdentityPDFUrl?.name ? (
                                                <div className="text-break">
                                                    <strong>Selected File:</strong> {values.IdentityPDFUrl.name}
                                                </div>
                                            ) : ""}
                                            <ErrorMessage name="IdentityPDFUrl" component="div" style={{ color: 'red', marginTop: '5px' }} />
                                            <ErrorMessage name="IdentityNo" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Date of Joining */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="DateOfJoining" className="form-label">
                                                Date of Joining <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                type="date"
                                                className="form-control"
                                                name="DateOfJoining"
                                                id="DateOfJoining"
                                                max={getCurrentDate()}
                                                onChange={(e) => { handleChange(e); }}
                                            />
                                            <ErrorMessage name="DateOfJoining" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Date of Exit */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="DateOfExit" className="form-label">Date of Exit</label>
                                            <Field
                                                type="date"
                                                className="form-control"
                                                name="DateOfExit"
                                                id="DateOfExit"
                                                min={values.DateOfJoining}
                                            />
                                            <ErrorMessage name="DateOfExit" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Zone */}
                                    {/* <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="ZoneId" className="form-label">
                                                Zone <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                as="select"
                                                name="ZoneId"
                                                id="ZoneId"
                                                className="form-select"
                                                onChange={(e) => {
                                                    const zoneId = e.target.value;
                                                    setFieldValue("ZoneId", zoneId, true);
                                                    setFieldError("ZoneId", undefined);
                                                    setFieldTouched("ZoneId", true, false);
                                                    setFieldValue("CircleId", "", false);
                                                    setFieldValue("WardId", "", false);
                                                    fetchCirclesByZone(zoneId);
                                                }}
                                            >
                                                {getActiveZones().map((zone) => (
                                                    <option key={zone.zoneId} value={String(zone.zoneId)}>
                                                        {zone.zoneName}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="ZoneId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="ZoneId" className="form-label">
                                                Zone <span style={{ color: '#EC1117' }}> *</span>
                                            </label>


                                            <Field name="ZoneId">
                                                {({ field, form }) => (

                                                    <Select
                                                        inputId="ZoneId"
                                                        name="ZoneId"
                                                        placeholder="Select Zone"
                                                        isSearchable
                                                        isClearable


                                                        options={getActiveZones().map((zone) => ({
                                                            value: String(zone.zoneId),
                                                            label: zone.zoneName,
                                                        }))}


                                                        value={
                                                            getActiveZones()
                                                                .map((zone) => ({
                                                                    value: String(zone.zoneId),
                                                                    label: zone.zoneName,
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        String(option.value) === String(field.value)
                                                                ) || null
                                                        }


                                                        onChange={(selectedOption) => {
                                                            const zoneId = selectedOption
                                                                ? selectedOption.value
                                                                : '';

                                                            form.setFieldValue("ZoneId", zoneId, true);
                                                            form.setFieldError("ZoneId", undefined);
                                                            form.setFieldTouched("ZoneId", true, false);

                                                            /* Same logic from your previous code */
                                                            form.setFieldValue("CircleId", "", false);
                                                            form.setFieldValue("WardId", "", false);

                                                            fetchCirclesByZone(zoneId);
                                                        }}

                                                        /* CHANGED: Handle Formik touched state */
                                                        onBlur={() => {
                                                            form.setFieldTouched("ZoneId", true);
                                                        }}

                                                        /* CHANGED: White searchable dropdown styling */
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),

                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: '#fff',
                                                                zIndex: 9999,
                                                            }),

                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused
                                                                    ? '#f0f0f0'
                                                                    : '#fff',
                                                                color: '#212529',
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>

                                            <ErrorMessage
                                                name="ZoneId"
                                                component="div"
                                                style={{ color: 'red' }}
                                            />
                                        </div>
                                    </Col>
                                    {/* Circle */}
                                    {/* <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="CircleId" className="form-label">
                                                Ward <span style={{ color: '#EC1117' }}> *</span>
                                            </label>
                                            <Field
                                                as="select"
                                                name="CircleId"
                                                id="CircleId"
                                                className="form-select"
                                            >
                                                <option value="">Select Ward</option>
                                                {circleOptions
                                                    .filter((c) => c.isActive !== false)
                                                    .map((circle) => (
                                                        <option key={circle.circleId} value={String(circle.circleId)}>
                                                            {circle.circleName}
                                                        </option>
                                                    ))}
                                            </Field>
                                            <ErrorMessage name="CircleId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}

                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="CircleId" className="form-label">
                                                Ward <span style={{ color: '#EC1117' }}> *</span>
                                            </label>

                                            <Field name="CircleId">
                                                {({ field, form }) => (
                                                    <Select
                                                        inputId="CircleId"
                                                        name="CircleId"
                                                        placeholder="Select Ward"
                                                        isSearchable
                                                        isClearable

                                                        options={circleOptions
                                                            .filter((c) => c.isActive !== false)
                                                            .map((circle) => ({
                                                                value: String(circle.circleId),
                                                                label: circle.circleName,
                                                            }))}

                                                        value={
                                                            circleOptions
                                                                .filter((c) => c.isActive !== false)
                                                                .map((circle) => ({
                                                                    value: String(circle.circleId),
                                                                    label: circle.circleName,
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        String(option.value) === String(field.value)
                                                                ) || null
                                                        }

                                                        onChange={(selectedOption) => {
                                                            form.setFieldValue(
                                                                'CircleId',
                                                                selectedOption ? selectedOption.value : ''
                                                            );
                                                        }}

                                                        onBlur={() => {
                                                            form.setFieldTouched('CircleId', true);
                                                        }}

                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: '#fff',
                                                                zIndex: 9999,
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused
                                                                    ? '#f0f0f0'
                                                                    : '#fff',
                                                                color: '#212529',
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>

                                            <ErrorMessage
                                                name="CircleId"
                                                component="div"
                                                style={{ color: 'red' }}
                                            />
                                        </div>
                                    </Col>

                                    {/* KPI Category */}
                                    {/* <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="KPICategoryId" className="form-label">
                                                KPI Category
                                            </label>
                                            <Field
                                                as="select"
                                                name="KPICategoryId"
                                                id="KPICategoryId"
                                                className="form-select"
                                            >
                                                <option value="">Select KPI Category</option>
                                                {kpiCategoryData &&
                                                    kpiCategoryData.map((kpiCategory) => (
                                                        <option
                                                            key={kpiCategory.kpitypeId}
                                                            value={kpiCategory.kpitypeId}
                                                        >
                                                            {kpiCategory.kpicode}
                                                        </option>
                                                    ))
                                                }
                                            </Field>
                                            <ErrorMessage name="KPICategoryId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="KPICategoryId" className="form-label">
                                                KPI Category
                                            </label>

                                            <Field name="KPICategoryId">
                                                {({ field, form }) => (
                                                    <Select
                                                        inputId="KPICategoryId"
                                                        name="KPICategoryId"
                                                        placeholder="Select KPI Category"
                                                        isSearchable
                                                        isClearable

                                                        options={
                                                            kpiCategoryData?.map((kpiCategory) => ({
                                                                value: String(kpiCategory.kpitypeId),
                                                                label: kpiCategory.kpicode,
                                                            })) || []
                                                        }

                                                        value={
                                                            kpiCategoryData
                                                                ?.map((kpiCategory) => ({
                                                                    value: String(kpiCategory.kpitypeId),
                                                                    label: kpiCategory.kpicode,
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        String(option.value) === String(field.value)
                                                                ) || null
                                                        }

                                                        onChange={(selectedOption) => {
                                                            form.setFieldValue(
                                                                'KPICategoryId',
                                                                selectedOption ? selectedOption.value : ''
                                                            );
                                                        }}

                                                        onBlur={() => {
                                                            form.setFieldTouched('KPICategoryId', true);
                                                        }}

                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: '#fff',
                                                                zIndex: 9999,
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused
                                                                    ? '#f0f0f0'
                                                                    : '#fff',
                                                                color: '#212529',
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>

                                            <ErrorMessage
                                                name="KPICategoryId"
                                                component="div"
                                                style={{ color: 'red' }}
                                            />
                                        </div>
                                    </Col>

                                    {/* Shift */}
                                    {/* <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="ShiftId" className="form-label">
                                                Shift
                                            </label>
                                            <Field
                                                as="select"
                                                name="ShiftId"
                                                id="ShiftId"
                                                className="form-select"
                                            >
                                                <option value="">Select Shift</option>
                                                {shiftsData &&
                                                    shiftsData
                                                        .filter(shift => shift.isActive)
                                                        .map((shift) => (
                                                            <option key={shift.shiftId} value={shift.shiftId}>
                                                                {shift.shiftName + " [" + shift.startTime + " - " + shift.endTime + "]"}
                                                            </option>
                                                        ))}
                                            </Field>
                                            <ErrorMessage name="ShiftId" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col> */}

                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="ShiftId" className="form-label">
                                                Shift
                                            </label>

                                            {/* CHANGED: Replaced <Field as="select"> with <Field name="ShiftId"> */}
                                            <Field name="ShiftId">
                                                {({ field, form }) => (
                                                    /* CHANGED: Added searchable react-select */
                                                    <Select
                                                        inputId="ShiftId"
                                                        name="ShiftId"
                                                        placeholder="Select Shift"
                                                        isSearchable
                                                        isClearable

                                                        /* CHANGED: Converted <option> into react-select options */
                                                        options={
                                                            shiftsData
                                                                ?.filter((shift) => shift.isActive)
                                                                .map((shift) => ({
                                                                    value: String(shift.shiftId),
                                                                    label:
                                                                        shift.shiftName +
                                                                        " [" +
                                                                        shift.startTime +
                                                                        " - " +
                                                                        shift.endTime +
                                                                        "]",
                                                                })) || []
                                                        }

                                                        /* CHANGED: Added selected value handling */
                                                        value={
                                                            shiftsData
                                                                ?.filter((shift) => shift.isActive)
                                                                .map((shift) => ({
                                                                    value: String(shift.shiftId),
                                                                    label:
                                                                        shift.shiftName +
                                                                        " [" +
                                                                        shift.startTime +
                                                                        " - " +
                                                                        shift.endTime +
                                                                        "]",
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        String(option.value) === String(field.value)
                                                                ) || null
                                                        }

                                                        /* CHANGED: Replaced normal select onChange */
                                                        onChange={(selectedOption) => {
                                                            form.setFieldValue(
                                                                "ShiftId",
                                                                selectedOption ? selectedOption.value : ""
                                                            );
                                                        }}

                                                        /* CHANGED: Added Formik touched handling */
                                                        onBlur={() => {
                                                            form.setFieldTouched("ShiftId", true);
                                                        }}

                                                        /* CHANGED: Added white dropdown styling */
                                                        styles={{
                                                            control: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#e9ecef",
                                                            }),
                                                            menu: (base) => ({
                                                                ...base,
                                                                backgroundColor: "#fff",
                                                                zIndex: 9999,
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isFocused
                                                                    ? "#f0f0f0"
                                                                    : "#fff",
                                                                color: "#212529",
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </Field>

                                            <ErrorMessage
                                                name="ShiftId"
                                                component="div"
                                                style={{ color: "red" }}
                                            />
                                        </div>
                                    </Col>

                                    {/* Status */}
                                    <Col md="6" lg="4">
                                        <label className="form-label">
                                            Status <span style={{ color: "#EC1117" }}> *</span>
                                        </label>
                                        <div className="form-group" style={{ display: "flex", alignItems: 'center' }}>
                                            <div className="form-check d-flex">
                                                <Field
                                                    type="radio"
                                                    className="form-check-input"
                                                    name="IsActive"
                                                    id="activeRadio"
                                                    value="true"
                                                />
                                                <label className="form-check-label" htmlFor="activeRadio">Active</label>
                                            </div>
                                            <div className="form-check ms-4 d-flex">
                                                <Field
                                                    type="radio"
                                                    className="form-check-input"
                                                    name="IsActive"
                                                    id="inactiveRadio"
                                                    value="false"
                                                />
                                                <label className="form-check-label" htmlFor="inactiveRadio">InActive</label>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Remarks */}
                                    <Col md="6" lg="4">
                                        <div className="form-group">
                                            <label htmlFor="Remarks" className="form-label">Remarks</label>
                                            <Field
                                                type="text"
                                                placeholder="Enter Remarks"
                                                className="form-control"
                                                name="Remarks"
                                                id="Remarks"
                                                maxLength="255"
                                            />
                                            <ErrorMessage name="Remarks" component="div" style={{ color: 'red' }} />
                                        </div>
                                    </Col>

                                    {/* Submit */}
                                    <Col lg="12" className="d-flex justify-content-center">
                                        <button
                                            type="submit"
                                            className="deployment-button"
                                            onClick={() => console.log('AddEditEmployee: submit clicked', formikRef.current?.values, formikRef.current?.errors)}
                                        >
                                            {isEditEmployee
                                                ? loading
                                                    ? <l-bouncy size="25" speed="1.75" color="white"></l-bouncy>
                                                    : <><i className="fa-solid fa-check done-container"></i> Update</>
                                                : loading
                                                    ? <l-bouncy size="25" speed="1.75" color="white"></l-bouncy>
                                                    : <><i className="fa-solid fa-check done-container"></i> Submit</>
                                            }
                                        </button>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </>
    );
};